import { DetailSections } from '../components/ui/DetailSections';
import { detailSections } from '../config/detailSections';
import { useDetailSection } from '../hooks/useDetailSection';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ArrowLeft,
  Loader,
  AlertCircle,
  UserCheck,
  MessageSquare,
  FileArchive,
  Server,
  Package,
  Calendar,
  Ticket,
  Activity,
  Images,
  Pencil,
  Trash2,
  Copy,
  Check,
  Plus,
  X,
  ChevronDown,
  Ruler,
  MapPin,
  ExternalLink,
  Search,
  Unlink,
  UserPlus,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';
import { useDebounce } from '../hooks/useDebounce';
import { cn } from '../utils/cn';
import { buildTeamApiService } from '../services/buildTeamApi';
import { usernameCacheService } from '../services/usernameCache';
import {
  BuildItemFull,
  BuildItemStatus,
  BuildTag,
  BuildProject,
  BUILD_ITEM_STATUSES,
} from '../types/buildTeam';
import { BuildStatusBadge } from '../components/buildteam/BuildStatusBadge';
import { BuildPriorityBadge } from '../components/buildteam/BuildPriorityBadge';
import { BuildAssignmentsSection } from '../components/buildteam/BuildAssignmentsSection';
import { BuildCommentsSection } from '../components/buildteam/BuildCommentsSection';
import { BuildSchematicsSection } from '../components/buildteam/BuildSchematicsSection';
import { BuildProjectGallerySection } from '../components/buildteam/BuildProjectGallerySection';
import { BuildServersSection } from '../components/buildteam/BuildServersSection';
import { BuildModpacksSection } from '../components/buildteam/BuildModpacksSection';
import { BuildCalendarSection } from '../components/buildteam/BuildCalendarSection';
import { BuildTicketsSection } from '../components/buildteam/BuildTicketsSection';
import { BuildActivityLog } from '../components/buildteam/BuildActivityLog';
import { ApogeeLinkedPackSummary } from '../components/apogee/ApogeeLinkedPackSummary';
import { EditBuildItemModal } from '../components/buildteam/EditBuildItemModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { CreatorProfile } from '../components/CreatorProfile';
import { format, formatDistanceToNow, isPast, differenceInDays } from 'date-fns';
import { KanbanLinkedCardsSection } from '../components/kanban/KanbanLinkedCardsSection';
import { PageHeader } from '../components/ui/PageHeader';
import { PanelTour } from '../components/ui/PanelTour';
import { buildWorkTour } from '../config/panelTours';
import { useTourNavigation } from '../hooks/useTourNavigation';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

type TabId = 'overview'
  | 'assignments'
  | 'comments'
  | 'schematics'
  | 'gallery'
  | 'servers'
  | 'modpacks'
  | 'calendar'
  | 'tickets'
  | 'activity';

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'overview', label: 'Brief & dates', icon: BookOpen },
  { id: 'assignments', label: 'Assignments', icon: UserCheck },
  { id: 'comments', label: 'Comments', icon: MessageSquare },
  { id: 'schematics', label: 'Schematics', icon: FileArchive },
  { id: 'gallery', label: 'Gallery', icon: Images },
  { id: 'servers', label: 'Servers', icon: Server },
  { id: 'modpacks', label: 'Modpacks', icon: Package },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
  { id: 'tickets', label: 'Tickets', icon: Ticket },
  { id: 'activity', label: 'Activity', icon: Activity },
];

function parseDimensionsDisplay(json: string | null): string | null {
  if (!json) return null;
  try {
    const o = JSON.parse(json) as Record<string, unknown>;
    const x = o.x != null ? String(o.x) : '?';
    const y = o.y != null ? String(o.y) : '?';
    const z = o.z != null ? String(o.z) : '?';
    return `${x} × ${y} × ${z}`;
  } catch {
    return null;
  }
}

function parseCoordinatesDisplay(json: string | null): { world: string; display: string } | null {
  if (!json) return null;
  try {
    const o = JSON.parse(json) as Record<string, unknown>;
    const world = o.world != null ? String(o.world) : 'unknown';
    const x = o.x != null ? String(o.x) : '0';
    const y = o.y != null ? String(o.y) : '0';
    const z = o.z != null ? String(o.z) : '0';
    return { world, display: `${world} (${x}, ${y}, ${z})` };
  } catch {
    return null;
  }
}

function parseMetadataDisplay(json: string | null): string | null {
  if (!json) return null;
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return null;
  }
}

function getDueDateStyle(dueDateUnix: number, status?: string): { className: string; label: string } {
  // Completed/cancelled work is never "overdue" or "due soon" — show the date neutrally.
  if (status === 'completed' || status === 'cancelled') {
    return { className: 'text-glass-400', label: '' };
  }
  const dueDate = new Date(dueDateUnix * 1000);
  if (isPast(dueDate)) {
    return { className: 'text-red-400', label: 'Overdue' };
  }
  const daysLeft = differenceInDays(dueDate, new Date());
  if (daysLeft <= 3) {
    return { className: 'text-amber-400', label: 'Due soon' };
  }
  return { className: 'text-glass-400', label: '' };
}

export const BuildItemDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isMobile = useIsMobile();
  const isDev = import.meta.env.DEV;

  const [build, setBuild] = useState<BuildItemFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useDetailSection(TABS, 'assignments');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [assignRequest, setAssignRequest] = useState(0);
  const { tourNavigation } = useTourNavigation();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  // Tag management
  const [allTags, setAllTags] = useState<BuildTag[]>([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [tagActionLoading, setTagActionLoading] = useState<string | null>(null);

  // Priority quick-change
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [priorityChanging, setPriorityChanging] = useState(false);

  // Move project
  const [showMovePanel, setShowMovePanel] = useState(false);
  const [moveSearchQuery, setMoveSearchQuery] = useState('');
  const debouncedMoveSearch = useDebounce(moveSearchQuery, 300);
  const [moveSearchResults, setMoveSearchResults] = useState<BuildProject[]>([]);
  const [moveSearchLoading, setMoveSearchLoading] = useState(false);
  const [moveLoading, setMoveLoading] = useState(false);

  const canEdit = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return (
      groups.includes('argus_build_panel') ||
      groups.includes('argus_build_lead') ||
      groups.includes('argus_build_admin') ||
      groups.includes('SPY_Super_admin')
    );
  }, [identity, isDev]);

  const canCreate = canEdit;

  const canDelete = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_build_admin') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const canLead = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return (
      groups.includes('argus_build_lead') ||
      groups.includes('argus_build_admin') ||
      groups.includes('SPY_Super_admin')
    );
  }, [identity, isDev]);

  useDocumentTitle(build ? `Build: ${build.title}` : 'Build Item Details');

  const loadBuild = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await buildTeamApiService.getBuildItemById(id);
      setBuild(data);

      const uuids = new Set<string>();
      if (data.created_by) uuids.add(data.created_by);
      if (data.updated_by) uuids.add(data.updated_by);
      data.latest_activity?.forEach((a) => uuids.add(a.actor_id));
      data.assignments?.forEach((a) => {
        uuids.add(a.assigned_by);
        uuids.add(a.member_id);
      });
      if (uuids.size > 0) {
        usernameCacheService.prefetchUsernames(Array.from(uuids));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load build item');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadBuild();
  }, [loadBuild]);

  useEffect(() => {
    buildTeamApiService.listTags().then(setAllTags).catch((err) => {
      console.error('Failed to load tags:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tags');
    });
  }, []);

  // Move project search
  useEffect(() => {
    if (!showMovePanel || debouncedMoveSearch.length < 1) {
      setMoveSearchResults([]);
      return;
    }
    let cancelled = false;
    setMoveSearchLoading(true);
    buildTeamApiService
      .searchProjects(debouncedMoveSearch)
      .then((res) => {
        if (!cancelled) setMoveSearchResults(res);
      })
      .catch(() => {
        if (!cancelled) setMoveSearchResults([]);
      })
      .finally(() => {
        if (!cancelled) setMoveSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedMoveSearch, showMovePanel]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = () => {
      setShowTagPicker(false);
      setShowPriorityDropdown(false);
      setShowStatusDropdown(false);
    };
    if (showTagPicker || showPriorityDropdown || showStatusDropdown) {
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClick, { once: true });
      }, 0);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClick);
      };
    }
  }, [showTagPicker, showPriorityDropdown, showStatusDropdown]);

  const handleAddTag = async (tagId: string) => {
    if (!id) return;
    setTagActionLoading(tagId);
    try {
      await buildTeamApiService.tagBuild(id, tagId);
      await loadBuild();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add tag');
    } finally {
      setTagActionLoading(null);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!id) return;
    setTagActionLoading(tagId);
    try {
      await buildTeamApiService.untagBuild(id, tagId);
      await loadBuild();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove tag');
    } finally {
      setTagActionLoading(null);
    }
  };

  const handlePriorityChange = async (newPriority: number) => {
    if (!id || !build) return;
    setPriorityChanging(true);
    setShowPriorityDropdown(false);
    try {
      await buildTeamApiService.updateBuildItem(id, { priority: newPriority });
      await loadBuild();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to change priority');
    } finally {
      setPriorityChanging(false);
    }
  };

  const handleStatusChange = async (newStatus: BuildItemStatus) => {
    if (!build || !id) return;
    setStatusChanging(true);
    setShowStatusDropdown(false);
    try {
      let reason: string | undefined;
      if (newStatus === 'cancelled') {
        reason = prompt('Enter cancellation reason:') || undefined;
        if (reason === undefined) {
          setStatusChanging(false);
          return;
        }
      }
      await buildTeamApiService.changeBuildItemStatus(id, newStatus, reason);
      await loadBuild();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to change status');
    } finally {
      setStatusChanging(false);
    }
  };

  const handleMoveProject = async (projectId: string | null) => {
    if (!id) return;
    setMoveLoading(true);
    try {
      await buildTeamApiService.moveBuildItemProject(id, projectId);
      setShowMovePanel(false);
      setMoveSearchQuery('');
      setMoveSearchResults([]);
      await loadBuild();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to move build item');
    } finally {
      setMoveLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await buildTeamApiService.deleteBuildItem(id);
      navigate('/build-team');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete build item');
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  const handleCopyId = () => {
    if (!id) return;
    navigator.clipboard.writeText(id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return '—';
    const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    return format(new Date(ms), 'MMM d, yyyy');
  };

  const formatDateRelative = (timestamp: number | null) => {
    if (!timestamp) return '—';
    const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    return formatDistanceToNow(new Date(ms), { addSuffix: true });
  };

  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true">
        {/* Header skeleton */}
        <div className="space-y-3">
          <SkeletonLoader className="h-3 w-24" />
          <div className="flex items-center gap-3">
            <SkeletonLoader className="h-7 w-64 max-w-full" />
            <SkeletonLoader className="h-5 w-20 rounded-full" />
            <SkeletonLoader className="h-5 w-10 rounded-full" />
          </div>
          <SkeletonLoader className="h-4 w-80 max-w-full" />
        </div>
        {/* Info cards */}
        <div className={cn('grid gap-4', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
          {[0, 1].map((i) => (
            <div key={i} className="glass-card p-4 md:p-5 space-y-3">
              <SkeletonLoader className="h-4 w-28" />
              <SkeletonLoader className="h-3 w-full" />
              <SkeletonLoader className="h-3 w-5/6" />
              <SkeletonLoader className="h-3 w-2/3" />
            </div>
          ))}
        </div>
        {/* Tabs */}
        <div className="glass-card">
          <div className="flex gap-4 px-4 py-3 border-b border-glass-200/10">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-4 w-16" />
            ))}
          </div>
          <div className="p-4 md:p-5 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-14 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !build) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Build Team"
          title="Build"
          backTo="/build-team"
          backLabel="Build Team"
        />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load build item"
          description={error || 'Build item not found'}
          action={
            <>
              <button onClick={loadBuild} className="glass-button">Retry</button>
              <button
                onClick={() => navigate('/build-team')}
                className="glass-button-secondary"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Build Team
              </button>
            </>
          }
        />
      </div>
    );
  }

  const dimensions = parseDimensionsDisplay(build.dimensions);
  const coordinates = parseCoordinatesDisplay(build.coordinates);
  const metadata = parseMetadataDisplay(build.metadata);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        className="relative z-10"
        eyebrow="Build Team"
        backTo="/build-team"
        backLabel="Build Team"
        title={build.title}
        titleAdornment={
          <>
            {/* Status */}
            <div className="relative flex items-center gap-1">
              <BuildStatusBadge status={build.status} type="build" size="md" />
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  disabled={statusChanging}
                  className="btn-ghost btn-xs"
                  title="Change status"
                  aria-label="Change status"
                  aria-expanded={showStatusDropdown}
                >
                  {statusChanging ? <Loader className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                  Change
                </button>
              )}
              {showStatusDropdown && (
                <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-50 min-w-[180px] py-1">
                  {BUILD_ITEM_STATUSES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => handleStatusChange(s)}
                      className={cn(
                        'w-full text-left px-3 py-1.5 text-sm hover:bg-glass-200/10 transition-colors',
                        s === build.status ? 'text-accent-300 bg-accent-500/10' : 'text-glass-200'
                      )}
                    >
                      {s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Priority */}
            <div className="relative flex items-center gap-1">
              <BuildPriorityBadge priority={build.priority} size="md" />
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                  disabled={priorityChanging}
                  className="btn-icon btn-icon-sm"
                  title="Change priority"
                  aria-label="Change priority"
                  aria-expanded={showPriorityDropdown}
                >
                  {priorityChanging ? <Loader className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              )}
              {showPriorityDropdown && (
                <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-50 min-w-[120px] max-h-[300px] overflow-y-auto py-1">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => handlePriorityChange(p)}
                      className={cn(
                        'w-full text-left px-3 py-1.5 text-sm tabular-nums hover:bg-glass-200/10 transition-colors',
                        p === build.priority ? 'text-accent-300 bg-accent-500/10' : 'text-glass-200'
                      )}
                    >
                      Priority {p}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {build.category && (
              <span className="badge bg-cyan-500/15 text-cyan-300 border-cyan-500/25">
                {build.category}
              </span>
            )}
          </>
        }
        description={build.description ? <span className="line-clamp-2">{build.description}</span> : undefined}
        actions={
          <>
            <button onClick={handleCopyId} className="glass-button-secondary" title="Copy ID" aria-label="Copy build ID">
              {copiedId ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              {copiedId ? 'Copied' : 'ID'}
            </button>
            <PanelTour
              tourId="build-work"
              title="Build"
              steps={buildWorkTour('build', canLead, canCreate, !!build.project_id)}
              enabled={!!build}
              {...tourNavigation}
            />
            {canLead && (
              <button
                type="button"
                data-tour="build-assign-quick"
                onClick={() => {
                  setActiveTab('assignments');
                  setAssignRequest((n) => n + 1);
                }}
                className="glass-button"
                title="Assign a builder to this build"
              >
                <UserPlus className="w-4 h-4" />
                Assign builder
              </button>
            )}
            {canEdit && (
              <button onClick={() => setIsEditOpen(true)} className="glass-button">
                <Pencil className="w-4 h-4" />
                Edit
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => setIsDeleteOpen(true)}
                className="btn-danger"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
          </>
        }
      >
        {build.apogee_id != null && (
          <div className="max-w-xl">
            <ApogeeLinkedPackSummary apogeeId={build.apogee_id} variant="compact" />
          </div>
        )}

        {/* Parent project + dimensions + coordinates */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-glass-400">
          {build.project ? (
            <div className="flex items-center gap-2 min-w-0">
              <span className="eyebrow">Project</span>
              <button
                onClick={() => navigate(`/build-team/projects/${build.project!.id}`)}
                className="inline-flex items-center gap-1 text-sm text-accent-300 hover:text-accent-200 hover:underline underline-offset-2 transition-colors font-medium min-w-0 truncate"
              >
                <ExternalLink className="w-3 h-3 shrink-0" />
                <span className="truncate">{build.project.title}</span>
              </button>
              {canEdit && (
                <button
                  onClick={() => {
                    setMoveSearchQuery('');
                    setMoveSearchResults([]);
                    setShowMovePanel(!showMovePanel);
                  }}
                  className="btn-ghost btn-xs"
                  aria-expanded={showMovePanel}
                >
                  Move
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="eyebrow">Project</span>
              <span className="text-xs text-glass-500">No parent project</span>
              {canEdit && (
                <button
                  onClick={() => {
                    setMoveSearchQuery('');
                    setMoveSearchResults([]);
                    setShowMovePanel(!showMovePanel);
                  }}
                  className="btn-ghost btn-xs"
                  aria-expanded={showMovePanel}
                >
                  <Plus className="w-3 h-3" />
                  Assign Project
                </button>
              )}
            </div>
          )}
          {dimensions && (
            <span className="inline-flex items-center gap-1.5 text-sm text-glass-300 font-mono tabular-nums">
              <Ruler className="w-3.5 h-3.5 text-glass-500" />
              {dimensions}
            </span>
          )}
          {coordinates && (
            <span className="inline-flex items-center gap-1.5 text-sm text-glass-300 tabular-nums">
              <MapPin className="w-3.5 h-3.5 text-glass-500" />
              {coordinates.display}
            </span>
          )}
        </div>

        {/* Move Project Panel */}
        {showMovePanel && (
          <div className="glass-card p-3 w-full max-w-md">
            <div className="flex items-center gap-2 mb-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
                <input
                  type="text"
                  value={moveSearchQuery}
                  onChange={(e) => setMoveSearchQuery(e.target.value)}
                  className="glass-input pl-9 pr-8 w-full"
                  placeholder="Search projects…"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setShowMovePanel(false);
                      setMoveSearchQuery('');
                      setMoveSearchResults([]);
                    }
                  }}
                />
                {moveSearchLoading && (
                  <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-glass-400" />
                )}
              </div>
              {build.project && (
                <button
                  onClick={() => handleMoveProject(null)}
                  disabled={moveLoading}
                  className="btn-danger whitespace-nowrap"
                >
                  <Unlink className="w-3.5 h-3.5" />
                  Detach
                </button>
              )}
              <button
                onClick={() => {
                  setShowMovePanel(false);
                  setMoveSearchQuery('');
                  setMoveSearchResults([]);
                }}
                className="btn-icon"
                title="Close"
                aria-label="Close project search"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {moveSearchQuery.length === 0 && (
              <p className="text-xs text-glass-500 px-1">Type to search…</p>
            )}
            {moveSearchResults.length > 0 && (
              <div className="space-y-0.5 max-h-[200px] overflow-y-auto">
                {moveSearchResults.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => handleMoveProject(project.id)}
                    disabled={moveLoading}
                    className={cn(
                      'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left hover:bg-glass-200/10 transition-colors',
                      project.id === build.project?.id && 'bg-accent-500/10'
                    )}
                  >
                    <div className="min-w-0">
                      <span className="text-sm text-white truncate block">{project.title}</span>
                      <span className="text-[11px] text-glass-500">{project.status}</span>
                    </div>
                    {project.id === build.project?.id && (
                      <span className="text-[11px] text-accent-300 flex-shrink-0">Current</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {debouncedMoveSearch.length >= 1 && !moveSearchLoading && moveSearchResults.length === 0 && (
              <p className="text-xs text-glass-500 px-1 py-2">No projects found</p>
            )}
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="eyebrow mr-0.5">Tags</span>
          {build.tags?.map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border group/tag"
              style={{
                backgroundColor: `${tag.color}20`,
                borderColor: `${tag.color}40`,
                color: tag.color,
              }}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: tag.color }}
              />
              {tag.name}
              {canEdit && (
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag.id)}
                  disabled={tagActionLoading === tag.id}
                  className={cn(
                    'ml-0.5 transition-opacity hover:brightness-125 touch-manipulation p-0.5',
                    isMobile ? 'opacity-100' : 'opacity-0 group-hover/tag:opacity-100 focus-visible:opacity-100'
                  )}
                  title="Remove tag"
                  aria-label={`Remove tag ${tag.name}`}
                >
                  {tagActionLoading === tag.id ? (
                    <Loader className="w-3 h-3 animate-spin" />
                  ) : (
                    <X className="w-3 h-3" />
                  )}
                </button>
              )}
            </span>
          ))}
          {!build.tags?.length && !canEdit && (
            <span className="text-xs text-glass-500">None</span>
          )}
          {canEdit && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTagPicker(!showTagPicker)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-dashed border-glass-200/25 text-glass-400 hover:text-white hover:border-glass-200/40 transition-colors touch-manipulation"
                aria-expanded={showTagPicker}
              >
                <Plus className="w-3 h-3" />
                Tag
              </button>
              {showTagPicker && (
                <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-20 min-w-[180px] max-h-[200px] overflow-y-auto py-1">
                  {(() => {
                    const currentTagIds = new Set(build.tags?.map((t) => t.id) || []);
                    const available = allTags.filter((t) => !currentTagIds.has(t.id));
                    if (available.length === 0) {
                      return (
                        <div className="px-3 py-2 text-xs text-glass-500">
                          No more tags available
                        </div>
                      );
                    }
                    return available.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => {
                          handleAddTag(tag.id);
                          setShowTagPicker(false);
                        }}
                        disabled={tagActionLoading === tag.id}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-glass-200/10 transition-colors flex items-center gap-2"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span className="text-glass-200">{tag.name}</span>
                      </button>
                    ));
                  })()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Dates + created/updated */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-glass-500 tabular-nums">
          {build.due_date && (() => {
            const style = getDueDateStyle(build.due_date, build.status);
            return (
              <span>Due: <span className={style.className}>{formatDate(build.due_date)}{style.label && <span className="ml-1">({style.label})</span>}</span></span>
            );
          })()}
          {build.started_at && (
            <span>Started: <span className="text-glass-200">{formatDate(build.started_at)}</span></span>
          )}
          {build.completed_at && (
            <span>Completed: <span className="text-glass-200">{formatDate(build.completed_at)}</span></span>
          )}
          <span className="inline-flex items-center gap-1">
            Created {formatDateRelative(build.created_at)} by <CreatorProfile creatorId={build.created_by} inline />
          </span>
          {build.updated_at && build.updated_by && (
            <span className="inline-flex items-center gap-1">
              Updated {formatDateRelative(build.updated_at)} by <CreatorProfile creatorId={build.updated_by} inline />
            </span>
          )}
        </div>
      </PageHeader>

      {/* Tabs */}
      <DetailSections sections={detailSections('build', TABS, { assignments: build.assignments?.length, comments: build.comments_count, schematics: build.schematics_count, servers: build.server_links?.length, modpacks: build.modpack_links?.length })} active={activeTab} onChange={setActiveTab} navTour="build-work-sections">
        {activeTab === 'overview' && (<>      {/* Info Cards */}
      <div className={cn('grid gap-4', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
        {/* Card 1 – Details */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Details" icon={Ruler} />
          <p className="text-sm text-glass-200 whitespace-pre-wrap mb-3">
            {build.description || 'No description provided'}
          </p>

          {dimensions && (
            <div className="flex items-center gap-3 mb-2">
              <span className="icon-tile icon-tile-sm">
                <Ruler className="w-3.5 h-3.5" />
              </span>
              <div>
                <span className="text-xs text-glass-400 block">Dimensions</span>
                <span className="text-sm text-glass-200 font-mono tabular-nums">{dimensions}</span>
              </div>
            </div>
          )}

          {coordinates && (
            <div className="flex items-center gap-3 mb-2">
              <span className="icon-tile icon-tile-sm">
                <MapPin className="w-3.5 h-3.5" />
              </span>
              <div>
                <span className="text-xs text-glass-400 block">Coordinates</span>
                <span className="text-sm text-glass-200 tabular-nums">{coordinates.display}</span>
              </div>
            </div>
          )}

          {build.category && (
            <div className="mb-2">
              <span className="text-xs text-glass-400 block mb-1">Category</span>
              <span className="badge bg-cyan-500/15 text-cyan-300 border-cyan-500/25">
                {build.category}
              </span>
            </div>
          )}

          {metadata && (
            <div className="mt-3 pt-3 border-t border-glass-200/10">
              <div className="eyebrow mb-1.5">Metadata</div>
              <pre className="subcard p-3 text-xs font-mono text-glass-300 overflow-x-auto">
                {metadata}
              </pre>
            </div>
          )}
        </div>

        {/* Card 2 – Key Dates */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Key Dates" icon={Calendar} />
          <dl className="space-y-2 text-sm">
            {([
              ['Due Date', build.due_date],
              ['Started At', build.started_at],
              ['Completed At', build.completed_at],
            ] as [string, number | null][]).map(([label, val]) => {
              const dueDateStyle = label === 'Due Date' && val ? getDueDateStyle(val, build.status) : null;
              return (
                <div key={label} className="flex justify-between gap-3">
                  <dt className="text-glass-400">{label}</dt>
                  <dd className={cn('tabular-nums', dueDateStyle ? dueDateStyle.className : 'text-glass-200')}>
                    {formatDate(val)}
                    {dueDateStyle?.label && (
                      <span className="ml-1.5 text-xs">({dueDateStyle.label})</span>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
          <div className="mt-3 pt-3 border-t border-glass-200/10 space-y-1 text-xs text-glass-500">
            <div className="flex justify-between gap-3">
              <span>Created</span>
              <span className="tabular-nums text-right">
                {formatDate(build.created_at)} by{' '}
                <CreatorProfile creatorId={build.created_by} inline />
              </span>
            </div>
            {build.updated_at && (
              <div className="flex justify-between gap-3">
                <span>Updated</span>
                <span className="tabular-nums text-right">
                  {formatDateRelative(build.updated_at)}
                  {build.updated_by && (
                    <> by <CreatorProfile creatorId={build.updated_by} inline /></>
                  )}
                </span>
              </div>
            )}
          </div>

          {build.project && (
            <div className="mt-3 pt-3 border-t border-glass-200/10">
              <span className="eyebrow block mb-1">Parent Project</span>
              <button
                onClick={() => navigate(`/build-team/projects/${build.project!.id}`)}
                className="inline-flex items-center gap-1 text-xs text-accent-300 hover:text-accent-200 hover:underline underline-offset-2 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                {build.project.title}
              </button>
            </div>
          )}
        </div>
      </div>

</>)}


          {activeTab === 'assignments' && (
            <BuildAssignmentsSection
              targetId={build.id}
              targetType="build"
              canLead={canLead}
              onUpdate={loadBuild}
              openAddFormSignal={assignRequest}
            />
          )}
          {activeTab === 'comments' && (
            <BuildCommentsSection
              targetId={build.id}
              targetType="build"
              canCreate={canCreate}
              canEdit={canEdit}
              canDelete={canDelete}
              onUpdate={loadBuild}
            />
          )}
          {activeTab === 'schematics' && (
            <BuildSchematicsSection
              targetId={build.id}
              targetType="build"
              canCreate={canCreate}
              canEdit={canEdit}
              canDelete={canDelete}
              onUpdate={loadBuild}
            />
          )}
          {activeTab === 'gallery' &&
            (build.project_id ? (
              <BuildProjectGallerySection
                projectId={build.project_id}
                canCreate={canCreate}
                canEdit={canEdit}
                canDelete={canDelete}
                onUpdate={loadBuild}
              />
            ) : (
              <div className="subcard text-sm text-glass-400 py-8 text-center px-4 border-dashed">
                This build is not linked to a project, so there is no shared gallery here. Images attached to comments
                still appear under those comments. Link the build to a project to upload and browse project gallery images.
              </div>
            ))}
          {activeTab === 'servers' && (
            <BuildServersSection
              targetId={build.id}
              targetType="build"
              canEdit={canEdit}
              canDelete={canDelete}
              onUpdate={loadBuild}
            />
          )}
          {activeTab === 'modpacks' && (
            <BuildModpacksSection
              targetId={build.id}
              targetType="build"
              canEdit={canEdit}
              canDelete={canDelete}
              onUpdate={loadBuild}
            />
          )}
          {activeTab === 'calendar' && (
            <BuildCalendarSection
              targetId={build.id}
              targetType="build"
              canEdit={canEdit}
              onUpdate={loadBuild}
            />
          )}
          {activeTab === 'tickets' && (
            <BuildTicketsSection
              targetId={build.id}
              targetType="build"
              canEdit={canEdit}
              onUpdate={loadBuild}
            />
          )}
          {activeTab === 'activity' && (
            <BuildActivityLog
              targetId={build.id}
              targetType="build"
            />
          )}

      </DetailSections>

      {/* Edit Modal */}
      {isEditOpen && (
        <EditBuildItemModal
          isOpen={isEditOpen}
          item={build}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            setIsEditOpen(false);
            loadBuild();
          }}
        />
      )}

      {/* Linked Kanban Cards */}
      {build && <KanbanLinkedCardsSection linkType="build" targetId={build.id} title="Linked Kanban Cards" />}

      {/* Delete Confirmation */}
      {isDeleteOpen && (
        <ConfirmationModal
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={handleDelete}
          title="Delete Build Item"
          message="Are you sure you want to delete this build item? This will also delete all associated assignments, comments, schematics links, server links, modpack links, calendar links, ticket links, and activity log entries. This action cannot be undone."
          confirmText="Delete"
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};
